import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary, Avatar, Box, Button, Chip,
  Container, Divider, Grid, IconButton, Link, Paper, Stack, Typography
} from '@mui/material';
import {
  ArrowForward, Assessment, Bolt, Check, CheckCircle, Computer, Download, ExpandMore, Instagram,
  Inventory2, MenuBook, Payments, PointOfSale, Print, Restaurant,
  Storefront, SupportAgent, TrendingUp, TwoWheeler, PhoneIphone,
  WhatsApp
} from '@mui/icons-material';

const ORANGE = '#ff6b35';
const NAVY = '#111827';
const GREEN = '#10b981';
const WHATSAPP_URL = 'https://wa.me/5587999927809?text=Ol%C3%A1%2C%20quero%20conhecer%20o%20All%20Delivery.';
const DESKTOP_APP_X64_URL = '/downloads/All-Delivery-Setup-1.0.5-x64.exe';
const DESKTOP_APP_X86_URL = '/downloads/All-Delivery-Setup-1.0.5-win7-ia32.exe';
const DESKTOP_APP_WIN7_X64_URL = '/downloads/All-Delivery-Setup-1.0.5-win7-x64.exe';
const DESKTOP_APP_WIN7_X86_URL = '/downloads/All-Delivery-Setup-1.0.5-win7-ia32.exe';

const benefits = [
  {
    icon: <PointOfSale />,
    title: 'Pedidos organizados',
    text: 'Receba, aceite e acompanhe cada pedido em um painel simples, sem depender de conversas perdidas.'
  },
  {
    icon: <Payments />,
    title: 'PIX e cartão online',
    text: 'Pagamento confirmado automaticamente e operação mais segura para sua equipe e seus clientes.'
  },
  {
    icon: <Print />,
    title: 'Impressão automática',
    text: 'O pedido chega pronto para produção, com itens, adicionais, endereço, pagamento e observações.'
  },
  {
    icon: <TwoWheeler />,
    title: 'Gestão de entregas',
    text: 'Acompanhe motoboys, corridas, taxas e status da entrega no mesmo ecossistema.'
  },
  {
    icon: <Inventory2 />,
    title: 'Estoque e caixa',
    text: 'Controle produtos, insumos, movimentações e fechamento diário sem planilhas separadas.'
  },
  {
    icon: <Assessment />,
    title: 'Números do negócio',
    text: 'Veja faturamento, pedidos e desempenho para tomar decisões com mais segurança.'
  }
];

const journey = [
  ['1', 'Crie sua loja', 'Faça o cadastro gratuito e receba acesso imediato ao painel.'],
  ['2', 'Monte o cardápio', 'Cadastre os produtos ou envie sua planilha, PDF ou foto para importação.'],
  ['3', 'Configure a operação', 'Defina horários, entrega, pagamento, impressão e equipe.'],
  ['4', 'Comece a vender', 'Publique a loja e receba pedidos dos clientes All Delivery.']
];

const faqs = [
  ['Quanto custa usar o All Delivery?', 'Os primeiros 30 dias são grátis. Depois, o plano custa R$ 59,90 por mês. Há uma taxa de 2% somente sobre os produtos pagos online pelo aplicativo; pagamentos recebidos diretamente pela loja não têm essa taxa.'],
  ['Preciso pagar para testar?', 'Não. Você pode usar a plataforma gratuitamente por 30 dias antes da primeira mensalidade.'],
  ['Posso usar meus próprios motoboys?', 'Sim. Você pode administrar os entregadores da própria loja e acompanhar as entregas pelo sistema.'],
  ['Funciona no celular e no computador?', 'Sim. O painel funciona pela web, enquanto clientes, motoboys e garçons contam com experiências próprias no aplicativo.'],
  ['Vocês ajudam a cadastrar o cardápio?', 'Sim. Você pode importar uma planilha e também enviar PDF ou foto para uma importação assistida com revisão.']
];

const Metric = ({ value, label, accent = ORANGE }) => (
  <Box>
    <Typography sx={{ fontSize: { xs: 26, md: 34 }, fontWeight: 900, color: accent, lineHeight: 1 }}>{value}</Typography>
    <Typography sx={{ mt: 0.7, color: '#64748b', fontSize: 13, fontWeight: 700 }}>{label}</Typography>
  </Box>
);

const DashboardPreview = () => (
  <Box sx={{ position: 'relative', minHeight: { xs: 440, md: 560 } }}>
    <Box
      sx={{
        position: 'absolute', inset: { xs: '30px 0 0', md: '25px 0 0 40px' },
        bgcolor: '#fff', borderRadius: { xs: 4, md: 6 }, boxShadow: '0 32px 90px rgba(15,23,42,.24)',
        overflow: 'hidden', border: '1px solid rgba(255,255,255,.7)'
      }}
    >
      <Stack direction="row" sx={{ height: '100%' }}>
        <Box sx={{ width: { xs: 58, md: 88 }, bgcolor: NAVY, py: 2.5, display: 'flex', alignItems: 'center', flexDirection: 'column', gap: 2.2 }}>
          <Avatar src="/logo.png" sx={{ width: { xs: 36, md: 46 }, height: { xs: 36, md: 46 }, bgcolor: ORANGE }} />
          {[Storefront, MenuBook, PointOfSale, Assessment].map((Icon, index) => (
            <Box key={index} sx={{ width: 38, height: 38, borderRadius: 2.5, display: 'grid', placeItems: 'center', color: index === 0 ? '#fff' : '#94a3b8', bgcolor: index === 0 ? ORANGE : 'transparent' }}>
              <Icon sx={{ fontSize: 19 }} />
            </Box>
          ))}
        </Box>
        <Box sx={{ flex: 1, bgcolor: '#f8fafc', p: { xs: 2, md: 3.5 }, minWidth: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography sx={{ fontWeight: 900, color: NAVY, fontSize: { xs: 17, md: 24 } }}>Visão geral</Typography>
              <Typography sx={{ color: '#94a3b8', fontSize: 11 }}>Sua operação hoje</Typography>
            </Box>
            <Chip label="Loja aberta" size="small" sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 800 }} />
          </Stack>
          <Grid container spacing={{ xs: 1, md: 1.5 }} sx={{ mt: 1.5 }}>
            {[
              ['Pedidos', '28', '#fff7ed', ORANGE],
              ['Vendas', 'R$ 1.842', '#ecfdf5', GREEN],
              ['Ticket médio', 'R$ 65,78', '#eff6ff', '#2563eb']
            ].map(([label, value, bg, color]) => (
              <Grid item xs={12} sm={4} key={label}>
                <Paper elevation={0} sx={{ p: { xs: 1.2, md: 2 }, borderRadius: 3, bgcolor: bg }}>
                  <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 700 }}>{label}</Typography>
                  <Typography sx={{ fontSize: { xs: 16, md: 21 }, color, fontWeight: 900, mt: .4 }}>{value}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
          <Paper elevation={0} sx={{ mt: 1.5, p: { xs: 1.5, md: 2.5 }, borderRadius: 3, bgcolor: '#fff' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography sx={{ fontWeight: 900, fontSize: { xs: 13, md: 16 }, color: NAVY }}>Pedidos por horário</Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: 10 }}>Movimento de hoje</Typography>
              </Box>
              <TrendingUp sx={{ color: GREEN }} />
            </Stack>
            <Stack direction="row" alignItems="end" spacing={{ xs: .7, md: 1.2 }} sx={{ height: { xs: 90, md: 135 }, mt: 1 }}>
              {[28, 42, 35, 58, 46, 85, 68, 100, 76, 60].map((height, index) => (
                <Box key={index} sx={{ flex: 1, height: `${height}%`, bgcolor: index === 7 ? ORANGE : '#fed7c7', borderRadius: '6px 6px 2px 2px' }} />
              ))}
            </Stack>
          </Paper>
          <Paper elevation={0} sx={{ mt: 1.5, p: 1.5, borderRadius: 3, bgcolor: '#fff' }}>
            <Stack direction="row" alignItems="center" spacing={1.4}>
              <Avatar sx={{ bgcolor: '#fff7ed', color: ORANGE }}><Restaurant /></Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 900, color: NAVY, fontSize: 12 }}>Novo pedido #1048</Typography>
                <Typography noWrap sx={{ color: '#94a3b8', fontSize: 10 }}>2x X-Burguer • Batata • Refrigerante</Typography>
              </Box>
              <Chip label="R$ 72,90" size="small" sx={{ fontWeight: 900, bgcolor: '#f1f5f9' }} />
            </Stack>
          </Paper>
        </Box>
      </Stack>
    </Box>
    <Paper
      sx={{
        position: 'absolute', top: 0, right: { xs: 6, md: -20 }, px: 2, py: 1.4,
        borderRadius: 3, boxShadow: '0 16px 35px rgba(15,23,42,.18)', zIndex: 2
      }}
    >
      <Stack direction="row" spacing={1.2} alignItems="center">
        <Box sx={{ width: 34, height: 34, display: 'grid', placeItems: 'center', borderRadius: 2, bgcolor: '#dcfce7', color: '#059669' }}><CheckCircle /></Box>
        <Box><Typography sx={{ fontSize: 11, color: '#64748b' }}>Pagamento confirmado</Typography><Typography sx={{ fontSize: 13, fontWeight: 900 }}>Pedido liberado</Typography></Box>
      </Stack>
    </Paper>
  </Box>
);

export default function LandingPage() {
  const navigate = useNavigate();
  const goSignup = () => navigate('/criar-loja');

  return (
    <Box sx={{ bgcolor: '#fff', color: NAVY, overflow: 'hidden' }}>
      <Box component="header" sx={{ position: 'absolute', width: '100%', zIndex: 10, pt: 2 }}>
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Avatar src="/logo.png" variant="rounded" sx={{ width: 42, height: 42, bgcolor: ORANGE }} />
              <Typography sx={{ color: '#fff', fontSize: 19, fontWeight: 900, letterSpacing: '-.5px' }}>All Delivery</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button onClick={() => navigate('/admin')} sx={{ color: '#cbd5e1', display: { xs: 'none', sm: 'inline-flex' }, fontWeight: 800 }}>Entrar</Button>
              <Button onClick={() => navigate('/pedir')} sx={{ color: '#cbd5e1', display: { xs: 'none', sm: 'inline-flex' }, fontWeight: 800 }}>Fazer pedido</Button>
              <Button onClick={goSignup} variant="contained" endIcon={<ArrowForward />} sx={{ bgcolor: ORANGE, borderRadius: 2.5, px: { xs: 1.6, sm: 2.4 }, fontWeight: 900, boxShadow: 'none', '&:hover': { bgcolor: '#f4511e' } }}>
                Criar minha loja
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Box
        component="section"
        sx={{
          position: 'relative', bgcolor: NAVY, color: '#fff', pt: { xs: 14, md: 18 }, pb: { xs: 10, md: 14 },
          '&::before': { content: '""', position: 'absolute', width: 620, height: 620, borderRadius: '50%', bgcolor: 'rgba(255,107,53,.12)', filter: 'blur(2px)', top: -260, right: -160 },
          '&::after': { content: '""', position: 'absolute', inset: 0, opacity: .24, backgroundImage: 'radial-gradient(rgba(255,255,255,.2) 1px, transparent 1px)', backgroundSize: '28px 28px', maskImage: 'linear-gradient(to right, transparent, black)' }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={{ xs: 7, md: 8 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Chip icon={<Bolt sx={{ color: `${ORANGE} !important` }} />} label="A plataforma do delivery local" sx={{ bgcolor: 'rgba(255,255,255,.08)', color: '#e2e8f0', border: '1px solid rgba(255,255,255,.12)', fontWeight: 800 }} />
              <Typography component="h1" sx={{ mt: 3, fontSize: { xs: 43, sm: 56, md: 68 }, lineHeight: .99, letterSpacing: '-3px', fontWeight: 950 }}>
                Seu delivery.<br /><Box component="span" sx={{ color: ORANGE }}>Sua marca.</Box><br />Mais resultado.
              </Typography>
              <Typography sx={{ mt: 3, maxWidth: 570, color: '#cbd5e1', fontSize: { xs: 17, md: 19 }, lineHeight: 1.65 }}>
                Organize pedidos, pagamentos, cardápio, estoque e entregas em uma plataforma feita para o dia a dia do seu estabelecimento.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4 }}>
                <Button onClick={goSignup} variant="contained" size="large" endIcon={<ArrowForward />} sx={{ bgcolor: ORANGE, borderRadius: 3, px: 3.2, py: 1.7, fontWeight: 900, fontSize: 16, boxShadow: '0 12px 30px rgba(255,107,53,.25)', '&:hover': { bgcolor: '#f4511e' } }}>
                  Começar 30 dias grátis
                </Button>
                <Button href={WHATSAPP_URL} target="_blank" rel="noreferrer" variant="outlined" size="large" startIcon={<WhatsApp />} sx={{ borderColor: 'rgba(255,255,255,.22)', color: '#fff', borderRadius: 3, px: 2.5, fontWeight: 800, '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,.05)' } }}>
                  Falar com a gente
                </Button>
              </Stack>
              <Stack direction="row" spacing={{ xs: 2, sm: 3 }} flexWrap="wrap" useFlexGap sx={{ mt: 3 }}>
                {['2% só em pagamentos online', 'Cancele quando quiser', 'Configuração assistida'].map((item) => (
                  <Stack key={item} direction="row" spacing={.7} alignItems="center">
                    <Check sx={{ color: GREEN, fontSize: 18 }} />
                    <Typography sx={{ color: '#cbd5e1', fontSize: 12, fontWeight: 700 }}>{item}</Typography>
                  </Stack>
                ))}
              </Stack>
              <Paper elevation={0} sx={{ mt: 4, p: 2, maxWidth: 520, borderRadius: 3, bgcolor: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.14)', color: '#fff' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
                  <Box>
                    <Typography fontWeight={900}>Quer fazer um pedido?</Typography>
                    <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 0.4 }}>
                      No iPhone, abra pelo Safari. Para deixar como aplicativo, use Compartilhar e depois “Adicionar à Tela de Início”.
                    </Typography>
                  </Box>
                  <Button onClick={() => navigate('/pedir')} variant="contained" startIcon={<PhoneIphone />} sx={{ flexShrink: 0, bgcolor: '#fff', color: NAVY, fontWeight: 900, '&:hover': { bgcolor: '#e2e8f0' } }}>
                    Abrir pedidos
                  </Button>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}><DashboardPreview /></Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={4}>
            <Typography sx={{ fontWeight: 900, fontSize: 15, color: '#475569' }}>Tudo que sua operação precisa, em um só lugar.</Typography>
          </Grid>
          <Grid item xs={4} md={2}><Metric value="2%" label="de comissão online" /></Grid>
          <Grid item xs={4} md={2}><Metric value="30 dias" label="para testar grátis" accent={GREEN} /></Grid>
          <Grid item xs={4} md={2}><Metric value="24h" label="sistema disponível" accent="#2563eb" /></Grid>
          <Grid item xs={12} md={2}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              <SupportAgent sx={{ color: ORANGE }} /><Typography sx={{ fontWeight: 900, fontSize: 14 }}>Suporte humano</Typography>
            </Stack>
          </Grid>
        </Grid>
      </Container>

      <Box component="section" sx={{ bgcolor: '#f8fafc', py: { xs: 9, md: 14 } }}>
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 720, mb: { xs: 5, md: 8 } }}>
            <Typography sx={{ color: ORANGE, fontWeight: 900, fontSize: 13, letterSpacing: '1.4px', textTransform: 'uppercase' }}>Uma operação mais leve</Typography>
            <Typography component="h2" sx={{ mt: 1.5, fontSize: { xs: 34, md: 52 }, lineHeight: 1.08, letterSpacing: '-2px', fontWeight: 950 }}>
              Menos improviso.<br />Mais controle para crescer.
            </Typography>
            <Typography sx={{ mt: 2.2, color: '#64748b', fontSize: 17, lineHeight: 1.7 }}>
              Do pedido recebido ao fechamento do caixa, cada etapa fica organizada para você focar no produto e no cliente.
            </Typography>
          </Box>
          <Grid container spacing={2.5}>
            {benefits.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.title}>
                <Paper elevation={0} sx={{ height: '100%', p: 3.5, borderRadius: 4, border: '1px solid #e8edf3', transition: '.25s ease', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 22px 45px rgba(15,23,42,.08)', borderColor: '#fed7c7' } }}>
                  <Box sx={{ width: 48, height: 48, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: '#fff1eb', color: ORANGE }}>{item.icon}</Box>
                  <Typography sx={{ mt: 2.5, fontSize: 19, fontWeight: 900 }}>{item.title}</Typography>
                  <Typography sx={{ mt: 1.2, color: '#64748b', lineHeight: 1.7, fontSize: 14 }}>{item.text}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 9, md: 14 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 6, md: 10 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography sx={{ color: GREEN, fontWeight: 900, fontSize: 13, letterSpacing: '1.4px', textTransform: 'uppercase' }}>Rápido para começar</Typography>
              <Typography component="h2" sx={{ mt: 1.5, fontSize: { xs: 34, md: 50 }, lineHeight: 1.08, letterSpacing: '-2px', fontWeight: 950 }}>
                Sua loja pronta em poucos passos.
              </Typography>
              <Typography sx={{ mt: 2, color: '#64748b', lineHeight: 1.7, fontSize: 17 }}>
                Você não precisa entender de tecnologia. O assistente mostra o que falta até sua loja estar pronta para publicar.
              </Typography>
              <Button onClick={goSignup} variant="contained" endIcon={<ArrowForward />} sx={{ mt: 3.5, bgcolor: NAVY, borderRadius: 2.5, px: 3, py: 1.4, fontWeight: 900, '&:hover': { bgcolor: '#25324a' } }}>
                Criar minha loja
              </Button>
            </Grid>
            <Grid item xs={12} md={7}>
              <Stack spacing={1.5}>
                {journey.map(([number, title, text], index) => (
                  <Paper key={number} elevation={0} sx={{ p: 2.3, borderRadius: 3.5, border: '1px solid #e8edf3', bgcolor: index === 0 ? '#fff7ed' : '#fff' }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ flexShrink: 0, width: 44, height: 44, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: index === 0 ? ORANGE : '#f1f5f9', color: index === 0 ? '#fff' : NAVY, fontWeight: 950 }}>{number}</Box>
                      <Box><Typography sx={{ fontWeight: 900, fontSize: 16 }}>{title}</Typography><Typography sx={{ color: '#64748b', fontSize: 13, mt: .3 }}>{text}</Typography></Box>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ bgcolor: NAVY, py: { xs: 9, md: 13 }, color: '#fff' }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Chip label="Plano profissional" sx={{ bgcolor: 'rgba(255,107,53,.14)', color: '#ff9a75', fontWeight: 900 }} />
              <Typography component="h2" sx={{ mt: 2.5, fontSize: { xs: 36, md: 54 }, lineHeight: 1.06, letterSpacing: '-2px', fontWeight: 950 }}>
                Preço simples.<br />Sem surpresa na venda.
              </Typography>
              <Typography sx={{ mt: 2, color: '#94a3b8', maxWidth: 520, fontSize: 17, lineHeight: 1.7 }}>
                Você sabe quanto vai pagar e continua com o valor das suas vendas, independentemente da quantidade de pedidos.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: { xs: 3, sm: 4.5 }, borderRadius: 5, bgcolor: '#fff', color: NAVY, position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 6, bgcolor: ORANGE }} />
                <Typography sx={{ fontWeight: 900, fontSize: 17 }}>All Delivery Profissional</Typography>
                <Stack direction="row" alignItems="baseline" sx={{ mt: 2 }}>
                  <Typography sx={{ color: '#64748b', fontWeight: 800 }}>R$</Typography>
                  <Typography sx={{ ml: .7, fontSize: { xs: 48, sm: 62 }, letterSpacing: '-3px', fontWeight: 950 }}>59,90</Typography>
                  <Typography sx={{ ml: 1, color: '#64748b', fontWeight: 700 }}>/mês</Typography>
                </Stack>
                <Stack spacing={1.4} sx={{ my: 3 }}>
                  {['Pedidos ilimitados', '2% sobre vendas online', 'Painel, app e gestão de entregas', '30 dias grátis para testar', 'Suporte direto pelo WhatsApp'].map((item) => (
                    <Stack direction="row" spacing={1.2} alignItems="center" key={item}><CheckCircle sx={{ color: GREEN, fontSize: 20 }} /><Typography sx={{ fontSize: 14, fontWeight: 700 }}>{item}</Typography></Stack>
                  ))}
                </Stack>
                <Button onClick={goSignup} fullWidth size="large" variant="contained" endIcon={<ArrowForward />} sx={{ bgcolor: ORANGE, borderRadius: 3, py: 1.6, fontWeight: 950, '&:hover': { bgcolor: '#f4511e' } }}>
                  Começar meu teste grátis
                </Button>
                <Typography sx={{ textAlign: 'center', mt: 1.5, color: '#94a3b8', fontSize: 11 }}>Comissão de 2% sobre produtos em pagamentos online. Cancele quando quiser.</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" id="aplicativo-windows" sx={{ py: { xs: 9, md: 13 }, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <Paper elevation={0} sx={{ p: { xs: 3, sm: 5, md: 7 }, borderRadius: 6, bgcolor: '#fff7ed', border: '1px solid #fed7aa', overflow: 'hidden', position: 'relative' }}>
            <Grid container spacing={{ xs: 4, md: 7 }} alignItems="center">
              <Grid item xs={12} md={7}>
                <Chip icon={<Computer />} label="Aplicativo para Windows" sx={{ bgcolor: '#fff', color: ORANGE, fontWeight: 900 }} />
                <Typography component="h2" sx={{ mt: 2.5, fontSize: { xs: 33, md: 48 }, lineHeight: 1.08, letterSpacing: '-1.8px', fontWeight: 950 }}>
                  Use o painel administrativo com impressao automatica.
                </Typography>
                <Typography sx={{ mt: 2, color: '#64748b', fontSize: 17, lineHeight: 1.7 }}>
                  No computador da lanchonete, instale o aplicativo oficial All Delivery. Ele abre o painel de pedidos e envia as comandas diretamente para a impressora padrao, sem abrir a tela de confirmacao.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3.5 }}>
                  <Button component="a" href={DESKTOP_APP_X64_URL} download variant="contained" size="large" startIcon={<Download />} sx={{ bgcolor: ORANGE, borderRadius: 3, px: 3, py: 1.5, fontWeight: 950, '&:hover': { bgcolor: '#f4511e' } }}>
                    Baixar Windows 10/11 (64 bits)
                  </Button>
                  <Button component="a" href={DESKTOP_APP_X64_URL} download variant="outlined" size="large" startIcon={<Download />} sx={{ borderColor: ORANGE, color: ORANGE, borderRadius: 3, px: 3, py: 1.5, fontWeight: 950 }}>
                    Legado (Windows 7 ou 32 bits)
                  </Button>
                  <Button component="a" href={DESKTOP_APP_X86_URL} download variant="outlined" size="large" startIcon={<Download />} sx={{ borderColor: ORANGE, color: ORANGE, borderRadius: 3, px: 3, py: 1.5, fontWeight: 950 }}>
                    Baixar Windows 32 bits
                  </Button>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1.5 }}>
                  <Button component="a" href={DESKTOP_APP_WIN7_X64_URL} download variant="outlined" size="small" startIcon={<Download />} sx={{ borderColor: '#64748b', color: '#475569' }}>
                    Windows 7 (64 bits)
                  </Button>
                  <Button component="a" href={DESKTOP_APP_WIN7_X86_URL} download variant="outlined" size="small" startIcon={<Download />} sx={{ borderColor: '#64748b', color: '#475569' }}>
                    Windows 7 (32 bits)
                  </Button>
                </Stack>
                <Typography sx={{ mt: 1.4, color: '#64748b', fontSize: 12 }}>
                  Use o instalador correspondente ao seu Windows. Windows 7 requer a versão legada; Windows 8 não é compatível.
                </Typography>
              </Grid>
              <Grid item xs={12} md={5}>
                <Stack spacing={1.5}>
                  {[
                    ['1', 'Baixe e instale', 'Abra o instalador e conclua os passos exibidos.'],
                    ['2', 'Defina a impressora padrao', 'Escolha nas configuracoes do Windows a impressora usada pela cozinha.'],
                    ['3', 'Abra o atalho All Delivery', 'Entre na conta da lanchonete e deixe o aplicativo aberto para receber pedidos.']
                  ].map(([number, title, text]) => (
                    <Paper key={number} elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: '#fff', border: '1px solid #ffedd5' }}>
                      <Stack direction="row" spacing={1.6} alignItems="center">
                        <Box sx={{ width: 38, height: 38, flexShrink: 0, borderRadius: '50%', bgcolor: ORANGE, color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 950 }}>{number}</Box>
                        <Box><Typography sx={{ fontWeight: 900 }}>{title}</Typography><Typography sx={{ color: '#64748b', fontSize: 13, mt: .3 }}>{text}</Typography></Box>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 9, md: 13 }, bgcolor: '#f8fafc' }}>
        <Container maxWidth="md">
          <Typography sx={{ textAlign: 'center', color: ORANGE, fontWeight: 900, fontSize: 13, letterSpacing: '1.4px', textTransform: 'uppercase' }}>Dúvidas frequentes</Typography>
          <Typography component="h2" sx={{ textAlign: 'center', mt: 1.5, mb: 5, fontSize: { xs: 34, md: 46 }, letterSpacing: '-1.5px', fontWeight: 950 }}>Antes de começar</Typography>
          <Stack spacing={1.3}>
            {faqs.map(([question, answer]) => (
              <Accordion key={question} elevation={0} disableGutters sx={{ border: '1px solid #e2e8f0', borderRadius: '16px !important', overflow: 'hidden', '&::before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 2.5, py: .8 }}><Typography sx={{ fontWeight: 900 }}>{question}</Typography></AccordionSummary>
                <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}><Typography sx={{ color: '#64748b', lineHeight: 1.7 }}>{answer}</Typography></AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 8, md: 11 } }}>
        <Container maxWidth="lg">
          <Paper elevation={0} sx={{ p: { xs: 4, md: 7 }, borderRadius: 6, color: '#fff', bgcolor: ORANGE, position: 'relative', overflow: 'hidden', '&::after': { content: '""', position: 'absolute', width: 360, height: 360, borderRadius: '50%', bgcolor: 'rgba(255,255,255,.10)', right: -100, top: -190 } }}>
            <Grid container spacing={4} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
              <Grid item xs={12} md={8}>
                <Typography sx={{ fontSize: { xs: 32, md: 48 }, lineHeight: 1.08, letterSpacing: '-2px', fontWeight: 950 }}>Sua loja pode começar hoje.</Typography>
                <Typography sx={{ mt: 1.5, color: 'rgba(255,255,255,.84)', fontSize: 17 }}>Crie sua conta, configure o cardápio e teste sem pagar nada por 30 dias.</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button onClick={goSignup} fullWidth size="large" variant="contained" endIcon={<ArrowForward />} sx={{ bgcolor: '#fff', color: NAVY, borderRadius: 3, py: 1.7, fontWeight: 950, '&:hover': { bgcolor: '#f8fafc' } }}>Criar minha loja</Button>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>

      <Box component="footer" sx={{ bgcolor: '#0b1220', color: '#fff', pt: 7, pb: 3 }}>
        <Container maxWidth="lg">
          <Grid container spacing={5}>
            <Grid item xs={12} md={5}>
              <Stack direction="row" spacing={1.2} alignItems="center"><Avatar src="/logo.png" variant="rounded" sx={{ width: 40, height: 40 }} /><Typography sx={{ fontWeight: 950, fontSize: 19 }}>All Delivery</Typography></Stack>
              <Typography sx={{ mt: 2, color: '#94a3b8', lineHeight: 1.7, maxWidth: 420 }}>Tecnologia simples para fortalecer estabelecimentos e entregas locais.</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography sx={{ fontWeight: 900, mb: 1.5 }}>Plataforma</Typography>
              <Stack spacing={1}><Link onClick={goSignup} component="button" color="#94a3b8" underline="hover">Criar loja</Link><Link href="/admin" color="#94a3b8" underline="hover">Entrar no painel</Link><Link href="/pedir" color="#94a3b8" underline="hover">App do cliente</Link></Stack>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography sx={{ fontWeight: 900, mb: 1.5 }}>Informações</Typography>
              <Stack spacing={1}><Link href="/legal" color="#94a3b8" underline="hover">Termos de uso</Link><Link href="/legal" color="#94a3b8" underline="hover">Privacidade</Link><Link href={WHATSAPP_URL} target="_blank" color="#94a3b8" underline="hover">Suporte</Link></Stack>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography sx={{ fontWeight: 900, mb: 1.5 }}>Fale com a gente</Typography>
              <Button href={WHATSAPP_URL} target="_blank" rel="noreferrer" variant="outlined" startIcon={<WhatsApp />} sx={{ color: '#fff', borderColor: '#334155', borderRadius: 2.5, fontWeight: 800 }}>Abrir WhatsApp</Button>
              <IconButton aria-label="Instagram" sx={{ ml: 1, color: '#94a3b8' }}><Instagram /></IconButton>
            </Grid>
          </Grid>
          <Divider sx={{ borderColor: '#1e293b', my: 4 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
            <Typography sx={{ color: '#64748b', fontSize: 12 }}>© {new Date().getFullYear()} All Delivery. Todos os direitos reservados.</Typography>
            <Typography sx={{ color: '#64748b', fontSize: 12 }}>Feito para o comércio local.</Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
